import api from "@/lib/axiosConfig";
import { Audiovisual } from "@/lib/types";
import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { RelativePosition } from "../dtos";

export const baseRoute = "back/api/audiovisuals";

interface AudiovisualDTO {
  where: any;
  order: any;
  take: number;
  skip: number;
}

async function getAudiovisuals({
  where,
  order,
  take,
  skip,
}: AudiovisualDTO): Promise<Audiovisual[]> {
  const { data } = await api.post(
    `back/api/audiovisuals?take=${take || 10}&skip=${skip || 0}`,
    {
      where,
      orderBy: order,
    },
  );
  return data;
}

export const useGetAllAudiovisuals = ({
  where,
  order,
  take,
  skip,
}: AudiovisualDTO) => {
  return useQuery({
    queryKey: ["audiovisuals", where, order, take, skip],
    queryFn: () => getAudiovisuals({ where, order, take, skip }),
    placeholderData: keepPreviousData,
  });
};

export async function getCountByStatus() {
  const { data } = await api.post(`back/api/audiovisuals/countByStatus`);
  return data;
}

export const useCountByStatus = () => {
  return useQuery({
    queryKey: ["countByStatus", "audiovisual"],
    queryFn: () => getCountByStatus(),
  });
};

export const getRelativePosition = async (
  id: string,
  order: object,
  where: object,
) => {
  const { data } = await api.post<RelativePosition>(
    `${baseRoute}/${id}/position`,
    {
      order,
      where,
    },
  );
  return data;
};
const getAudiovisual = async (id: string) => {
  const { data } = await api.get<Audiovisual>(`back/api/audiovisuals/${id}`);
  return data;
};

export const useGetAudiovisual = (id?: string) => {
  return useQuery({
    queryKey: ["audiovisual", id],
    queryFn: () => (id ? getAudiovisual(id) : undefined),
  });
};

async function updateAudiovisual(audiovisual: Audiovisual) {
  const {
    instanceId: _iid,
    updatedById: _ubid,
    createdById: _cbid,
    ...rest
  } = audiovisual;
  return await api.patch(`back/api/audiovisuals/${audiovisual.id}`, {
    data: rest,
  });
}

export function useUpdateAudiovisual() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAudiovisual,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["audiovisual", v.id] });
      queryClient.invalidateQueries({ queryKey: ["audiovisuals"] });
    },
  });
}

type CreateAudiovisualParams = {
  audiovisuelDescriptionNumeroEnregistrement: string;
  audiovisuelDescriptionTitre: string;
};
type CreateAudiovisualResponse = Pick<Audiovisual, "id">;

const createAudiovisual = async ({
  audiovisuelDescriptionNumeroEnregistrement,
  audiovisuelDescriptionTitre,
}: CreateAudiovisualParams) => {
  return await api
    .post<CreateAudiovisualResponse>("back/api/audiovisuals/create", {
      audiovisuelDescriptionNumeroEnregistrement,
      audiovisuelDescriptionTitre,
    })
    .then((res) => res.data);
};

export const useCreateAudiovisual = (
  options?: UseMutationOptions<
    CreateAudiovisualResponse,
    Error,
    CreateAudiovisualParams
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: createAudiovisual,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["audiovisuals"] });
      queryClient.invalidateQueries({ queryKey: ["audiovisual", data.id] });
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "audiovisual"],
      });
      queryClient.invalidateQueries({ queryKey: ["instance", "count-data"] });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
};

export const getAllIds = async (where?: object) => {
  const { data } = await api.post<{ id: string }[]>(`${baseRoute}/ids`, {
    where,
  });
  return data;
};
